import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trophy, Users, Calendar, Mail, Lock, User, Eye, EyeOff, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// --- PALETA DE CORES (Branding) ---
const COLORS = {
  green: '#084A2C',      // Verde Escuro Principal
  gold: '#D4AF37',       // Dourado
  goldHover: '#B5952F',  // Dourado mais escuro para hover
  white: '#FFFFFF',
  grayBg: '#F8FAFC',     // Cinza muito sutil para áreas secundárias
  textDark: '#111827',   // Quase preto para leitura
  textGray: '#6B7280'
};

const HomePage = () => {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();
  
  // States
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Benefícios listados ao lado do form
  const benefits = [
    'Gestão completa de campeonatos',
    'Perfil verificado de atleta',
    'Estatísticas em tempo real'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const response = await login(formData.email, formData.password);
        if (response.success) {
          navigate('/feed');
        } else {
          setError(response.error || 'Credenciais inválidas');
        }
      } else {
        const response = await register({
          name: formData.name,
          email: formData.email,
          password: formData.password,
        });
        if (response.success) {
          navigate('/feed');
        } else {
          setError(response.error || 'Erro ao criar conta');
        }
      }
    } catch (err) {
      setError('Ocorreu um erro inesperado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-green-100 selection:text-green-900">
      
      {/* --- NAVBAR CLEAN --- */}
      <nav className="bg-white border-b border-gray-100 py-4 px-6 fixed w-full top-0 z-50 bg-opacity-95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/vukz-logo.jpeg"
              alt="VUKZ Logo"
              className="h-10 md:h-12 w-auto object-contain"
            />
            <div className="flex flex-col">
              <span className="text-lg md:text-xl font-bold" style={{ color: COLORS.green }}>Arena Stats</span>
              <span className="text-[10px] md:text-xs text-gray-500 hidden sm:block">Onde o esporte acontece e fica registrado</span>
            </div>
          </div>

          <div className="hidden md:flex gap-8 text-sm font-medium text-gray-600">
            <Link to="#" className="hover:text-[#084A2C] transition-colors">Funcionalidades</Link>
            <Link to="#" className="hover:text-[#084A2C] transition-colors">Planos</Link>
            <Link to="#" className="hover:text-[#084A2C] transition-colors">Ajuda</Link>
          </div>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <main className="pt-28 pb-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* LADO ESQUERDO: Conteúdo e Formulário */}
          <div>
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-100 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#D4AF37]"></span>
                <span className="text-xs font-bold uppercase tracking-wider text-[#084A2C]">Nova Temporada 2025</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-[#111827] leading-tight">
                Onde o esporte <br className="hidden sm:block" />
                <span className="relative inline-block">
                  <span className="relative z-10" style={{ color: COLORS.green }}>acontece e fica registrado</span>
                  {/* Sublinhado Dourado estilizado */}
                  <svg className="absolute bottom-0 left-0 w-full h-3 -mb-1 text-[#D4AF37] opacity-60 z-0" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                  </svg>
                </span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-6 max-w-md">
                Junte-se a maior comunidade esportiva. Acompanhe seu desempenho, gerencie ligas e destaque-se no ranking.
              </p>

              {/* Lista de Benefícios (Checkmarks) */}
              <div className="flex flex-col gap-3 mb-8">
                {benefits.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-700 font-medium">
                    <CheckCircle2 size={18} color={COLORS.green} />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* FORMULÁRIO ESTILIZADO (Card) */}
            <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-gray-100 overflow-hidden max-w-md">
              <div className="p-1 h-1 w-full" style={{ background: `linear-gradient(90deg, ${COLORS.green} 0%, ${COLORS.gold} 100%)` }}></div>
              
              <div className="p-8">
                {/* Se já estiver autenticado, mostrar botão para ir ao feed */}
                {isAuthenticated ? (
                  <div className="text-center py-8">
                    <CheckCircle2 size={48} className="mx-auto mb-4" style={{ color: COLORS.green }} />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Você já está conectado!</h3>
                    <p className="text-gray-600 mb-6">Pronto para continuar sua jornada esportiva?</p>
                    <Link
                      to="/feed"
                      className="inline-block w-full py-3 px-6 rounded-lg font-bold text-white transition-all shadow-md hover:shadow-lg"
                      style={{ backgroundColor: COLORS.green }}
                    >
                      Ir para o Feed
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-gray-900">
                        {isLogin ? 'Bem-vindo de volta' : 'Crie sua conta'}
                      </h2>
                      <button 
                        onClick={() => {
                          setIsLogin(!isLogin);
                          setError('');
                        }}
                        className="text-sm font-semibold hover:underline"
                        style={{ color: COLORS.gold }}
                      >
                        {isLogin ? 'Criar conta' : 'Entrar'}
                      </button>
                    </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-700 uppercase">Nome</label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 text-gray-400" size={18} />
                        <input
                          type="text"
                          className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#084A2C] focus:ring-1 focus:ring-[#084A2C] outline-none transition-all"
                          placeholder="Seu nome"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-700 uppercase">E-mail</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
                      <input
                        type="email"
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#084A2C] focus:ring-1 focus:ring-[#084A2C] outline-none transition-all"
                        placeholder="nome@exemplo.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-700 uppercase">Senha</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
                      <input
                        type={showPassword ? "text" : "password"}
                        className="w-full pl-10 pr-12 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#084A2C] focus:ring-1 focus:ring-[#084A2C] outline-none transition-all"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-lg font-bold text-white shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 mt-4"
                    style={{ backgroundColor: COLORS.green }}
                  >
                    {loading ? 'Processando...' : (isLogin ? 'Acessar Arena' : 'Começar Agora')}
                    {!loading && <ArrowRight size={18} />}
                  </button>
                </form>
                
                {/* Divisor Social Login (Opcional) */}
                <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                  <p className="text-xs text-gray-400">
                    Ao continuar, você concorda com nossos Termos de Uso.
                  </p>
                </div>
                </>
                )}
              </div>
            </div>
          </div>

          {/* LADO DIREITO: Imagem Hero */}
          <div className="hidden lg:block relative h-full min-h-[600px]">
            {/* Elemento Decorativo Atrás (Quadrado Dourado Vazado) */}
            <div 
                className="absolute top-10 right-10 w-full h-full border-2 rounded-3xl opacity-30 z-0 transform translate-x-4 translate-y-4"
                style={{ borderColor: COLORS.gold }}
            ></div>

            {/* Elemento Decorativo (Círculo Verde) */}
            <div 
                className="absolute bottom-[-20px] left-[-20px] w-32 h-32 rounded-full z-0 opacity-10"
                style={{ backgroundColor: COLORS.green }}
            ></div>

            {/* A Imagem Principal */}
            <div className="relative z-10 w-full h-full rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                <div className="absolute inset-0 bg-gradient-to-t from-[#084A2C] via-transparent to-transparent opacity-40 z-20 mix-blend-multiply"></div>
                <img 
                    src="/imagem-home-page.jpg" 
                    alt="Atletas Arena Stats" 
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700 ease-in-out"
                />
                
                {/* Badge Flutuante sobre a imagem */}
                <div className="absolute bottom-8 left-8 z-30 bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-lg border-l-4" style={{ borderLeftColor: COLORS.gold }}>
                    <div className="flex items-center gap-3">
                        <div className="bg-green-100 p-2 rounded-full">
                            <Users size={20} color={COLORS.green} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Comunidade</p>
                            <p className="text-lg font-bold text-gray-900">+15.000 Atletas</p>
                        </div>
                    </div>
                </div>
            </div>
          </div>

        </div>
      </main>

      {/* FOOTER BAR (Statísticas Rápidas) */}
      <div className="bg-[#F9FAFB] border-t border-gray-200 py-10">
         <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
                { label: 'Esportes', val: '10+', icon: Trophy },
                { label: 'Competições', val: '220+', icon: Calendar },
                { label: 'Clubes', val: '341', icon: Users },
                { label: 'Países', val: '3', icon: Calendar },
            ].map((stat, i) => (
                <div key={i} className="flex flex-col items-center justify-center text-center group cursor-default">
                    <stat.icon size={32} className="mb-3 text-gray-300 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
                    <span className="text-3xl font-bold text-[#084A2C]">{stat.val}</span>
                    <span className="text-sm text-gray-500 font-medium uppercase tracking-wide mt-1">{stat.label}</span>
                </div>
            ))}
         </div>
      </div>

      {/* Microsoft for Startups */}
      <div className="bg-white py-12 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center text-center">
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Construído com apoio de</p>
            <div className="flex items-center gap-4">
              <img 
                src="/microsoft-logo.png" 
                alt="Microsoft for Startups" 
                className="h-10 w-auto object-contain hover:scale-105 transition-all"
              />
              <div className="text-left">
                <p className="font-bold text-gray-800 text-base">Microsoft for Startups</p>
                <p className="text-xs text-gray-500">Empowering Innovation</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <footer className="bg-white py-6 text-center border-t border-gray-100">
        <p className="text-sm text-gray-400">© 2025 Arena Stats. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
};

export default HomePage;
